import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface SeoBreadcrumb {
  label: string;
  to: string;
}

interface BreadcrumbsProps {
  items: SeoBreadcrumb[];
}

const localePrefixFromPath = (pathname: string) => {
  const match = pathname.match(/^\/(en|it|es|fr|de)(?=\/|$)/);
  return match?.[1] || '';
};

const withLocalePrefix = (path: string, pathname: string) => {
  if (/^(https?:)?\/\//.test(path) || path.startsWith('#')) {
    return path;
  }

  const prefix = localePrefixFromPath(pathname);
  if (!prefix || path.match(/^\/(en|it|es|fr|de)(?=\/|$)/)) {
    return path;
  }

  return path === '/' ? `/${prefix}` : `/${prefix}${path}`;
};

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  const location = useLocation();
  const { t } = useTranslation('common');

  if (items.length <= 1) {
    return null;
  }

  return (
    <nav aria-label={t('navigation.breadcrumb')} className="text-xs font-semibold text-on-surface-variant">
      <ol className="flex flex-wrap items-center gap-1.5">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.to}-${item.label}`} className="flex items-center gap-1.5">
              {index > 0 ? <span aria-hidden="true" className="text-primary-500/70">/</span> : null}
              {isLast ? (
                <span aria-current="page" className="inline-flex min-h-8 items-center rounded-full bg-primary-50 px-3 py-1 text-on-surface shadow-sm">
                  {item.label}
                </span>
              ) : (
                <Link
                  to={withLocalePrefix(item.to, location.pathname)}
                  className="inline-flex min-h-8 items-center rounded-full px-3 py-1 text-primary-700 transition-colors hover:bg-primary-50 hover:text-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
